import type { EmailOtpType } from '@supabase/supabase-js';
import { supabase } from './supabase';

export type FinishAuthResult =
  | { ok: true }
  | { ok: false; error: string };

/**
 * Conclui login após magic link (OTP do app, generate_link do script, ou email).
 * Suporta: ?code= (PKCE), #access_token= (implicit), ?token_hash= (verify).
 */
export async function finishAuthFromUrl(): Promise<FinishAuthResult> {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));

  const authError = search.get('error_description') ?? search.get('error') ?? hash.get('error_description') ?? hash.get('error');
  if (authError) {
    return { ok: false, error: authError };
  }

  const code = search.get('code');
  if (code) {
    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) return { ok: false, error: error.message };
  }

  const token_hash = search.get('token_hash');
  const type = search.get('type') as EmailOtpType | null;
  if (token_hash && type) {
    const { error } = await supabase.auth.verifyOtp({ token_hash, type });
    if (error) return { ok: false, error: error.message };
  }

  const access_token = hash.get('access_token');
  const refresh_token = hash.get('refresh_token');
  if (access_token && refresh_token) {
    const { error } = await supabase.auth.setSession({ access_token, refresh_token });
    if (error) return { ok: false, error: error.message };
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (session) return { ok: true };

  return waitForAuthSession(12_000);
}

function waitForAuthSession(timeoutMs: number): Promise<FinishAuthResult> {
  return new Promise((resolve) => {
    let settled = false;

    const done = (result: FinishAuthResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      sub.subscription.unsubscribe();
      resolve(result);
    };

    const timer = setTimeout(
      () => done({ ok: false, error: 'Sessão não encontrada após o redirect. Gere um link novo e tente de novo.' }),
      timeoutMs,
    );

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
        done({ ok: true });
      }
    });

    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) done({ ok: true });
    });
  });
}

export function clearAuthParamsFromUrl() {
  const path = window.location.pathname;
  window.history.replaceState({}, document.title, path);
}
